//--------------------------------------------------------------------------
// <copyright file="HardwareIDDialog.js" company="Microsoft">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
//--------------------------------------------------------------------------
import FluentPrimaryButton from './FluentPrimaryButton';
import FluentTextField from './FluentTextField';
import { useState } from 'react'
import MicrosoftLogo from './MicrosoftLogo';
import BackButton from './BackButton';
import { ProgressIndicator } from '@fluentui/react/lib/ProgressIndicator';
import PairingStates from '../models/PairingStatesEnum';
import { webApiServicePost } from "../services/webAPIServicePost";
import { webApiConfig, tokenRequest } from '../utils/authConfig';
import FluentDropdown from './FluentDropdown';

/// <summary>
/// Helper methods and display components related to Pairing code dialog.
/// </summary>
const HardwareIDDialog = ({ username, setPairingState, pairingState, setHardwareId, hardwareId, logout, acquireToken, correlationId, selectedOption, setSelectedOption, errorMessages, setErrorMessages }) => {

  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const pairDevicePostRequestCallback = async (response) => {
    if (response.ok) {
      setHardwareId('');
      setSelectedOption(null);
      setPairingState(PairingStates.PairingInProgress);
    }
    else {
      var responseMessage = await response.text();
      var parsedResponse = JSON.parse(responseMessage);
      setErrorMessages(parsedResponse);
      setPairingState(PairingStates.PairingAuthorizationFailed);
    }
  }

  const callPairDeviceWebApi = async () => {

    var token = null;
    try {
      token = await acquireToken(tokenRequest);
    } catch (error) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessages('We encountered some issue. Please try again in sometime');
      return null;
    }
    try {
      const requestBody = JSON.stringify({
        HardwareId: hardwareId.toUpperCase(),
        Region: selectedOption.key.toUpperCase()
      });
      const responsePair = await webApiServicePost(webApiConfig.BaseDeploymentURL + webApiConfig.webAPIHardwareIdPairPath, token.accessToken, requestBody, correlationId);
      return await pairDevicePostRequestCallback(responsePair);

    } catch (error) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessages('We encountered some issue. Please try again in sometime');
    }
  }

  const handleFormSubmit = async () => {
    if (!validateHardwareId() && selectedOption == null) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessages('Please enter a valid hardware ID and select region to start pairing.');
    }
    else if (selectedOption == null) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessages('Please select a region to start pairing.');
    }
    else if (!validateHardwareId()) {
      setPairingState(PairingStates.PairingCodeError);
      setErrorMessages('Please enter a valid hardware ID to start pairing.');
    }
    else{
      setIsButtonDisabled(true);
      setPairingState(PairingStates.PairingStatusPending);
      await callPairDeviceWebApi();
      setIsButtonDisabled(false);
    }
  };

  const validateHardwareId = () => {
    if(hardwareId.length == 0){
      return false;
    }

    // Trim the input to remove leading and trailing whitespace
    const hardwareIdTrimmed = hardwareId.trim();
    setHardwareId(hardwareIdTrimmed);

    // Split the trimmed input string by ':'
    const parts = hardwareIdTrimmed.split(':');

    // Check if there are more than 2 parts
    if (parts.length > 2) {
      return false;
    }
    
    // Get the first part
    const firstPart = parts[0];
  
    // Check the first part length and character pattern
    if (
      firstPart.length <= 20 &&
      /^[0-9a-zA-Z-]+$/.test(firstPart)
    ) {
      // If there are two parts, also check the second part
      if (parts.length === 2) {
        const secondPart = parseInt(parts[1]);
        // Check if the second part is a number between 1 and 999
        if (!isNaN(secondPart) && secondPart >= 1 && secondPart <= 999) {
          return true;
        }
      } else {
        return true; // Only the first part is valid
      }
    }
  
    return false;
  }

  const handleButtonPress = async () => {
    await handleFormSubmit();
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      await handleFormSubmit();
    }
  };

  var paragraph = null;
  var error = null;

  switch (pairingState) {
    case PairingStates.PairingCodeNotEntered:
      paragraph =
        <div>
          <div>
            <p data-testid="message" className="Dialog-paragraph-hardwareiddialog">Select the region and enter the hardware ID</p>
            <FluentDropdown setSelectedOption={setSelectedOption} selectedOption={selectedOption} />
            <FluentTextField setInput={setHardwareId} input={hardwareId} onKeyDownFunction={handleKeyDown} placeholderText={"Enter hardware ID"} shouldAutoFocus={false}  />
          </div>
          <div className="Dialog-footer-hardwareiddialog">
            <a className="Dialog-feedback" rel="noreferrer noopener" target="_blank" href="https://feedbackportal.microsoft.com/feedback/">Give feedback</a>
            <FluentPrimaryButton text='Pair' cssClassName='Dialog-button' setPairingState={setPairingState} onClickFunction={handleButtonPress} isButtonDisabled={isButtonDisabled} />
          </div>
        </div>;

      break;
    case PairingStates.PairingCodeError:
      paragraph =
        <div>
          <p role="alert" data-testid="message-error" className="Dialog-paragraph-error display-linebreak">{errorMessages}</p>
          <div>
          <p data-testid="message" className="Dialog-paragraph-hardwareiddialog">Select the region and enter the hardware ID</p>
            <FluentDropdown setSelectedOption={setSelectedOption} selectedOption={selectedOption} />
            <FluentTextField setInput={setHardwareId} input={hardwareId} onKeyDownFunction={handleKeyDown} placeholderText={"Enter hardware ID"} shouldAutoFocus={false} />
          </div>
          <div className="Dialog-footer-hardwareiddialog">
            <a className="Dialog-feedback" rel="noreferrer noopener" target="_blank" href="https://feedbackportal.microsoft.com/feedback/">Give feedback</a>
            <FluentPrimaryButton text='Pair' cssClassName='Dialog-button' setPairingState={setPairingState} onClickFunction={handleButtonPress} isButtonDisabled={isButtonDisabled} />
          </div>
        </div>;
      break;
    case PairingStates.PairingStatusPending:
      paragraph =
        <div>
          <ProgressIndicator data-testid="loadingIndicator" className="Dialog-inprogress" styles={{ root: { width: '352px' }, progressTrack: { backgroundColor: '#FFFFFF', } }} />
          <div>
          <p data-testid="message" className="Dialog-paragraph-hardwareiddialog">Select the region and enter the hardware ID</p>
            <FluentDropdown setSelectedOption={setSelectedOption} selectedOption={selectedOption} />
            <FluentTextField setInput={setHardwareId} input={hardwareId} onKeyDownFunction={handleKeyDown} placeholderText={"Enter hardware ID"} shouldAutoFocus={false}  />
          </div>;

        </div>;

      break;
    default:
  }

  return (
    <div className="Dialog-hardwareid" data-testid="pairingCodeDialog">
      <MicrosoftLogo />
      <BackButton onClickFunction={logout} />
      <p data-testid="username" className="Dialog-username">{username}</p>
      <h1 className="Dialog-header">Enter details to add a device</h1>
      <div className="Dialog-flexible">
        {paragraph}
      </div>
    </div>
  )
}

export default HardwareIDDialog
